import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Building2, Loader2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useInviteBusiness } from '~/entities/session/model/use-admin-auth'
import { BusinessInfoFields, EmailField, NameFields } from '~/features/auth/auth-form-fields'
import * as m from '~/paraglide/messages'
import { inviteBusinessSchema } from '~/shared/types/admin'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'

export const Route = createFileRoute('/admin/invite-business')({
  component: InviteBusinessPage,
})

function InviteBusinessPage() {
  const navigate = useNavigate()
  const inviteBusiness = useInviteBusiness()
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [isClub, setIsClub] = useState(false)

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
      legalName: '',
      registrationNumber: '',
    },
    onSubmit: async ({ value }) => {
      await inviteBusiness
        .mutateAsync({
          ...value,
          registrationNumber: isClub ? null : value.registrationNumber,
        })
        .then(() => navigate({ to: '/admin', search: { highlight: value.legalName } }))
        .catch(() => undefined)
    },
  })

  const validateForm = () => {
    const schema = isClub
      ? inviteBusinessSchema.omit({ registrationNumber: true })
      : inviteBusinessSchema
    return schema.safeParse(form.state.values).success
  }

  return (
    <div className="p-4 py-8 sm:p-8 sm:py-24">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 mb-6 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {m.backToBusinessList()}
        </Link>

        <Card>
          <CardHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex justify-center items-center bg-blue-600 rounded-lg w-12 h-12">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">{m.createNewBusiness()}</CardTitle>
                <CardDescription>{m.createNewBusinessDescription()}</CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (validateForm()) {
                  form.handleSubmit()
                }
              }}
              className="space-y-6"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <UserPlus className="w-4 h-4" />
                  <h3 className="font-medium">{m.personalInformation()}</h3>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <NameFields
                    form={form}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </div>

                <EmailField
                  form={form}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Building2 className="w-4 h-4" />
                  <h3 className="font-medium">{m.companyInformation()}</h3>
                </div>

                <BusinessInfoFields
                  form={form}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  isClub={isClub}
                  onIsClubChange={setIsClub}
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">{m.inviteBusinessInfo()}</p>
              </div>

              {inviteBusiness.isError && !inviteBusiness.isPending && (
                <div className="bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
                  <p className="text-sm text-destructive">
                    {inviteBusiness.error instanceof Error
                      ? inviteBusiness.error.message
                      : m.createBusinessError()}
                  </p>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/admin' })}
                  className="flex-1"
                >
                  {m.cancel()}
                </Button>
                <Button
                  type="submit"
                  disabled={inviteBusiness.isPending}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {inviteBusiness.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {m.creating()}
                    </>
                  ) : (
                    m.createBusiness()
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
